/** Create / edit dialog for a preflight checklist group (react-hook-form + zod).
 *  Groups are system-wide (no tenant); PATCH is partial. */
import { Box, Button, TextField } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import { groups, type PreflightGroup, type PreflightGroupWrite } from '@/api/preflight';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

const FormSchema = z.object({
  code: z.string().min(1, 'Required'),
  name: z.string().min(1, 'Required'),
  description: z.string(),
  sequence: z.number({ invalid_type_error: 'Required' }).int('Whole number').min(0, 'Must be ≥ 0'),
});

type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = { code: '', name: '', description: '', sequence: 0 };

function toDefaults(group: PreflightGroup | null): FormValues {
  if (!group) return EMPTY;
  return {
    code: group.code ?? '',
    name: group.name ?? '',
    description: group.description ?? '',
    sequence: group.sequence ?? 0,
  };
}

interface Props {
  open: boolean;
  /** null = create; a record = edit. */
  group: PreflightGroup | null;
  onClose: () => void;
}

export function PreflightGroupDialog({ open, group, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const isEdit = group !== null;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toDefaults(group),
  });

  // Re-seed the form whenever the dialog opens for a different record.
  useEffect(() => {
    if (open) reset(toDefaults(group));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group?.id]);

  const mutation = useMutation({
    mutationFn: (body: PreflightGroupWrite) =>
      isEdit ? groups.update(group.id, body) : groups.create(body),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Group updated.' : 'Group created.', 'success');
      qc.invalidateQueries({ queryKey: ['preflight', 'groups'] });
      onClose();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const submit = handleSubmit((v) =>
    mutation.mutate({
      code: v.code.trim(),
      name: v.name.trim(),
      description: v.description.trim(),
      sequence: v.sequence,
    }),
  );

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit group · ${group.name}` : 'New checklist group'}
      disableBackdropClose
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={mutation.isPending} onClick={submit}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create group'}
          </Button>
        </>
      }
    >
      <Box component="form" onSubmit={submit} noValidate sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          {...register('code')}
          label="Code"
          size="small"
          fullWidth
          error={Boolean(errors.code)}
          helperText={errors.code?.message ?? 'Stable machine identifier (e.g. setup).'}
        />
        <TextField
          {...register('name')}
          label="Name"
          size="small"
          fullWidth
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
        />
        <TextField
          {...register('description')}
          label="Description"
          size="small"
          fullWidth
          multiline
          minRows={2}
          error={Boolean(errors.description)}
          helperText={errors.description?.message}
        />
        <TextField
          {...register('sequence', { valueAsNumber: true })}
          label="Sequence"
          size="small"
          type="number"
          sx={{ width: 160 }}
          error={Boolean(errors.sequence)}
          helperText={errors.sequence?.message ?? 'Display order.'}
        />
      </Box>
    </AppDialog>
  );
}
